import { HELP_PANEL_ATTR, stepAtElement, type AnchoredStep } from "./dom";
import type { HelpStep } from "./types";

/**
 * The interaction half of "point at a field" — plain DOM, no React, so its one safety promise
 * can be tested on its own: asking what a control does is never the same as pressing it.
 *
 * Every listener is on `window` in the CAPTURE phase, so the page's own handlers never see the
 * event. The whole press sequence is swallowed, not just `click`: Fluent's Combobox opens on
 * `mousedown` and a menu item commits on `mouseup`. The guide's own surfaces
 * (`data-help-panel`) are exempt, so its buttons keep working.
 */

export interface InspectHandlers {
  /** The pointer moved onto a different documented control (or off every one: `null`). */
  onHover: (found: AnchoredStep | null) => void;
  /** A documented control was clicked. */
  onPin: (found: AnchoredStep) => void;
  /** A click landed on something no step documents — at this viewport position. */
  onMiss: (at: { top: number; left: number }) => void;
  /** Escape. */
  onExit: () => void;
}

const PRESS_EVENTS = [
  "pointerdown",
  "pointerup",
  "mousedown",
  "mouseup",
  "dblclick",
  "contextmenu",
];

/** Attaches the listeners and returns the function that removes them. */
export const attachInspectListeners = (
  index: Map<string, HelpStep>,
  handlers: InspectHandlers
): (() => void) => {
  const own = (target: EventTarget | null): boolean =>
    target instanceof Element && target.closest(`[${HELP_PANEL_ATTR}]`) !== null;

  /** The element the pointer last resolved to, so a move over the SAME control is free. */
  let hovered: HTMLElement | null = null;

  const onMove = (event: PointerEvent): void => {
    const found = stepAtElement(event.target, index);
    if ((found?.element ?? null) === hovered) return;
    hovered = found?.element ?? null;
    handlers.onHover(found);
  };

  const swallow = (event: Event): void => {
    if (own(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const onClick = (event: MouseEvent): void => {
    if (own(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
    const found = stepAtElement(event.target, index);
    // Said out loud rather than ignored: a click that does nothing reads as the mode being
    // broken, when the truth is that this control is not documented yet.
    if (found) handlers.onPin(found);
    else handlers.onMiss({ top: event.clientY, left: event.clientX });
  };

  const onKey = (event: KeyboardEvent): void => {
    if (event.key !== "Escape") return;
    // Capture + stop, like the tour: leaving must not also dismiss the dialog underneath.
    event.stopPropagation();
    event.preventDefault();
    handlers.onExit();
  };

  window.addEventListener("pointermove", onMove, true);
  window.addEventListener("click", onClick, true);
  window.addEventListener("keydown", onKey, true);
  for (const name of PRESS_EVENTS) window.addEventListener(name, swallow, true);

  return () => {
    window.removeEventListener("pointermove", onMove, true);
    window.removeEventListener("click", onClick, true);
    window.removeEventListener("keydown", onKey, true);
    for (const name of PRESS_EVENTS) window.removeEventListener(name, swallow, true);
  };
};
